<template>
  <b-container fluid>
    <b-row>
      <b-col class="p-4">
        <h2 class="text-left">Users
          <span class="float-right">
            <user-form :text="'Create'" :user="newUser" v-on:ok="createUser"></user-form>
          </span>
        </h2>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="p-4">
        <b-input-group>
          <b-form-input
            v-model="searchText"
            placeholder="Search"
            type="text"
            debounce="500">
          </b-form-input>
          <b-input-group-append>
            <b-button v-on:click="searchText = ''"><b-icon-x></b-icon-x></b-button>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
     <b-row>
       <b-col>
         <b-form-checkbox v-model="onlyNewUsers" switch class="ml-2">Only New Users</b-form-checkbox>
         <b-form-checkbox v-model="includeDisabledUsers" switch class="ml-2">Include Disabled Users</b-form-checkbox>
       </b-col>
       <b-col class="text-right">
        <b-pagination v-model="pagination.page" :total-rows="pagination.total" :per-page="pagination.pageSize" align="right" v-if="!loading" @change="filterData"></b-pagination>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="p-4">
        <b-table small :items="users" :fields="fields" :busy="loading" class="text-left">
          <template v-slot:table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>Loading...</strong>
            </div>
          </template>

          <template v-slot:head(name)="data">
            <b-icon id="filter-name" :icon="nameFilter.text === '' ? 'funnel' : 'funnel-fill'"></b-icon>
            {{data.label}}
            <b-icon @click="sort(nameFilter)" :icon="nameFilter.sortOrder === 1 ? 'arrow-up' : nameFilter.sortOrder === 2 ? 'arrow-down': 'arrow-down-up'"></b-icon>
            <b-popover target="filter-name" triggers="click" placement="bottom">
              <template v-slot:title>Filter by Name</template>
              <b-form-group label="Name">
                <b-input type="text" v-model="nameFilter.text"></b-input>
              </b-form-group>
              <b-form-group>
                <b-form-select v-model="nameFilter.queryOperator">
                  <b-form-select-option :value="0"></b-form-select-option>
                  <b-form-select-option :value="1">Equals</b-form-select-option>
                  <b-form-select-option :value="2">Contains</b-form-select-option>
                  <b-form-select-option :value="3">Starts With</b-form-select-option>
                  <b-form-select-option :value="4">Ends With</b-form-select-option>
                </b-form-select>
              </b-form-group>
              <b-button-group>
                <b-button @click="clearFilter('nameFilter', { sortOrder: 0, queryOperator: 0, text: '' })">Clear</b-button>
                <b-button @click="filterData(1)">Filter</b-button>
                <b-button @click="$root.$emit('bv::hide::popover')">Cancel</b-button>
              </b-button-group>
            </b-popover>
          </template>

          <template v-slot:head(email)="data">
            <b-icon id="filter-email" :icon="emailFilter.text === '' ? 'funnel' : 'funnel-fill'"></b-icon>
            {{data.label}}
            <b-icon @click="sort(emailFilter)" :icon="emailFilter.sortOrder === 1 ? 'arrow-up' : emailFilter.sortOrder === 2 ? 'arrow-down': 'arrow-down-up'"></b-icon>
            <b-popover target="filter-email" triggers="click" placement="bottom">
              <template v-slot:title>Filter by Email</template>
              <b-form-group label="Email">
                <b-input type="text" v-model="emailFilter.text"></b-input>
              </b-form-group>
              <b-form-group>
                <b-form-select v-model="emailFilter.queryOperator">
                  <b-form-select-option :value="0"></b-form-select-option>
                  <b-form-select-option :value="1">Equals</b-form-select-option>
                  <b-form-select-option :value="2">Contains</b-form-select-option>
                  <b-form-select-option :value="3">Starts With</b-form-select-option>
                  <b-form-select-option :value="4">Ends With</b-form-select-option>
                </b-form-select>
              </b-form-group>
              <b-button-group>
                <b-button @click="clearFilter('emailFilter', { sortOrder: 0, queryOperator: 0, text: '' })">Clear</b-button>
                <b-button @click="filterData(1)">Filter</b-button>
                <b-button @click="$root.$emit('bv::hide::popover')">Cancel</b-button>
              </b-button-group>
            </b-popover>
          </template>

          <template v-slot:head(company)="data">
            <b-icon id="filter-company" :icon="companyFilter.text === '' ? 'funnel' : 'funnel-fill'"></b-icon>
            {{data.label}}
            <b-icon @click="sort(companyFilter)" :icon="companyFilter.sortOrder === 1 ? 'arrow-up' : companyFilter.sortOrder === 2 ? 'arrow-down': 'arrow-down-up'"></b-icon>
            <b-popover target="filter-company" triggers="click" placement="bottom">
              <template v-slot:title>Filter by Company</template>
              <b-form-group label="Company">
                <b-input type="text" v-model="companyFilter.text"></b-input>
              </b-form-group>
              <b-form-group>
                <b-form-select v-model="companyFilter.queryOperator">
                  <b-form-select-option :value="0"></b-form-select-option>
                  <b-form-select-option :value="1">Equals</b-form-select-option>
                  <b-form-select-option :value="2">Contains</b-form-select-option>
                  <b-form-select-option :value="3">Starts With</b-form-select-option>
                  <b-form-select-option :value="4">Ends With</b-form-select-option>
                </b-form-select>
              </b-form-group>
              <b-button-group>
                <b-button @click="clearFilter('companyFilter', { sortOrder: 0, queryOperator: 0, text: '' })">Clear</b-button>
                <b-button @click="filterData(1)">Filter</b-button>
                <b-button @click="$root.$emit('bv::hide::popover')">Cancel</b-button>
              </b-button-group>
            </b-popover>
          </template>

          <template v-slot:cell(name)="data">
            <b-button variant="link" :to="{ name: 'User', params: { id: data.item.id }}">{{data.value}}</b-button>
          </template>

          <template v-slot:cell(email)="data">
            {{data.value}}
          </template>

          <template v-slot:cell(company)="data">
            {{data.value}}
          </template>

          <template v-slot:cell(roles)="data">
            {{data.value.join()}}
          </template>

          <template v-slot:cell(requiresPasswordReset)="data">
            <b-form-checkbox
              v-model="data.value"
              disabled
            >
            </b-form-checkbox>
          </template>

          <template v-slot:cell(passwordResetToken)="data">
            {{data.value}}
          </template>

          <template v-slot:cell(lastLogin)="data">
            {{data.value | formatDate('MMM DD, YYYY')}}
          </template>

          <template v-slot:cell(disabled)="data">
            <b-form-checkbox
              v-model="data.value"
              disabled
            >
            </b-form-checkbox>
          </template>

          <template v-slot:cell(actions)="data">
            <user-form style="display: inline;" :text="'Edit'" :user="data.item" v-on:ok="saveUser"></user-form>
            <b-button @click="sendEmail(data.item.id)" style="display: inline;" variant="link" :disabled="sending">Send Email</b-button>
            <b-button @click="deleteUser(data.item)" variant="link" style="display: inline;">Delete</b-button>
          </template>
        </b-table>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="text-right">
        <b-pagination v-model="pagination.page" :total-rows="pagination.total" :per-page="pagination.pageSize" align="right" v-if="!loading" @change="filterData"></b-pagination>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import Vue from 'vue'
import moment from 'moment-timezone'
import Api from '@/api/Api'
import UserForm from './components/UserForm'

Vue.filter('formatDate', function (value, format) {
  if (moment(value).isValid() && moment(value).year() > 1) {
    if (format) {
      return moment(value).format(format)
    } else {
      return moment(value).format('MMMM Do YYYY hh:mm a')
    }
  } else {
    return ''
  }
})

export default {
  name: 'UsersPage',
  data () {
    return {
      fields: [
        { key: 'name', label: 'Name' }, 'email',
        { key: 'company', label: 'Company' }, 'roles', 'requiresPasswordReset', 'passwordResetToken', 'lastLogin', 'disabled', { key: 'actions', label: '' } ],
      users: [],
      loading: true,
      searchText: '',
      onlyNewUsers: false,
      includeDisabledUsers: false,
      sending: false,
      newUser: {
        id: 0,
        name: '',
        email: '',
        roles: [],
        type: '',
        requiresPasswordReset: true,
        disabled: false,
        password: '',
        confirmPassword: '',
        phoneNumber: null
      },
      pagination: {
        pageSize: 25,
        page: 1,
        total: 0
      },
      nameFilter: {
        sortOrder: 0,
        queryOperator: 0,
        text: ''
      },
      companyFilter: {
        sortOrder: 0,
        queryOperator: 0,
        text: ''
      },
      emailFilter: {
        sortOrder: 0,
        queryOperator: 0,
        text: ''
      }
    }
  },
  created () {
    this.filterData(1)
  },
  methods: {
    filterData (page) {
      this.$root.$emit('bv::hide::popover')
      var vm = this
      vm.loading = true
      vm.pagination.page = page
      Api().post('Users/Query', {
        page: vm.pagination.page,
        pageSize: vm.pagination.pageSize,
        nameFilter: vm.nameFilter,
        companyFilter: vm.companyFilter,
        emailFilter: vm.emailFilter,
        onlyNewUsers: vm.onlyNewUsers,
        includeDisabledUsers: vm.includeDisabledUsers
      })
        .then((r) => {
          vm.users = r.data.items.map((u) => {
            return {
              ...u,
              updatePassword: false
            }
          })
          vm.pagination.total = r.data.total
          vm.pagination.pageSize = r.data.pageSize
        })
        .catch((e) => {
          if (e.response.status === 401) {
            this.$router.push({ name: 'Login' })
          } else {
            console.log(e)
          }
        })
        .finally(() => {
          vm.loading = false
        })
    },
    createUser (result) {
      console.log(result)
      var vm = this
      vm.loading = true
      Api().post('Users', result.user)
        .then((r) => {
          vm.users.push(r.data)
        })
        .finally(() => {
          vm.loading = false
          vm.newUser = {
            id: 0,
            name: '',
            email: '',
            roles: [],
            type: '',
            phoneNumber: '',
            requiresPasswordReset: true,
            disabled: false
          }
        })
    },
    saveUser (result) {
      var vm = this
      vm.loading = true
      console.log(result)
      Api().put('Users/' + result.user.id, result.user)
        .then((r) => {
          vm.users.splice(vm.users.findIndex((n) => n.id === result.user.id), 1, r.data)
        })
        .finally(() => {
          vm.loading = false
        })
    },
    sendEmail (id) {
      var vm = this
      vm.sending = true
      Api().post('Mail/SendWelcomeEmail/' + id)
        .then((r) => {
          vm.$bvToast.toast('Mail Sent', {
            toaster: 'b-toaster-top-center',
            variant: 'success',
            autoHideDelay: 5000
          })
        })
        .finally(() => {
          vm.sending = false
        })
    },
    deleteUser (user) {
      this.$bvModal.msgBoxConfirm('Are you sure you want to delete this user and all of their data?', {
        title: 'Delete User?',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      })
        .then(value => {
          if (value) {
            var vm = this
            vm.loading = true
            Api().delete('Users/' + user.id)
              .then((r) => {
                vm.users.splice(vm.users.findIndex((i) => { return i.id === user.id }), 1)
              })
              .catch((e) => {
                console.log(e)
              })
              .finally(() => {
                vm.loading = false
              })
          }
        })
    },
    clearFilter (filterName, filter) {
      this[filterName] = filter
      this.filterData(1)
    },
    sort (filter) {
      if (filter.sortOrder === 0) {
        filter.sortOrder = 1
      } else if (filter.sortOrder === 1) {
        filter.sortOrder = 2
      } else {
        filter.sortOrder = 0
      }
      this.filterData(1)
      console.log(filter)
    }
  },
  watch: {
    searchText (value) {
      this.nameFilter.text = value
      this.nameFilter.queryOperator = 2
      this.filterData(1)
    },
    onlyNewUsers (value) {
      this.filterData(1)
    },
    includeDisabledUsers (value) {
      this.filterData(1)
    }
  },
  components: {
    UserForm
  }
}
</script>

<style>

</style>
